import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { logout } from '~/auth/slices/auth.slice'
import { RootState } from '~/redux/store'
import { TeacherAccount, TeacherProfile, TeacherSchool } from '~/classroom/types/classroom.types'
import { toTeacherProfile } from '~/classroom/utils/teacherProfile'

/**
 * Who is signed in to the teacher panel, kept from the login response — the only endpoint that
 * carries it, since the classes endpoint answers `{ classes }` and nothing else.
 *
 * ⚠ Persisted (whitelisted in `store.ts`) because the access token outlives a reload, and an
 * identity that did not would leave a teacher signed in with no avatar. Display data only —
 * nothing is authorised from it. Cleared by the `logout` action, not by the menus, so a new
 * sign-out site cannot forget to.
 */
interface SchoolTeacherState {
  account: TeacherAccount | null
  school: TeacherSchool | null
}

const initialState: SchoolTeacherState = {
  account: null,
  school: null,
}

const slice = createSlice({
  name: 'schoolTeacher',
  initialState,
  reducers: {
    teacherSignedIn: (
      state,
      { payload }: PayloadAction<{ account: TeacherAccount; school: TeacherSchool }>,
    ): SchoolTeacherState => ({ ...state, account: payload.account, school: payload.school }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
  },
})

export const { teacherSignedIn } = slice.actions

export const selectTeacherAccount = (state: RootState): TeacherAccount | null => state.schoolTeacher.account

export const selectTeacherSchool = (state: RootState): TeacherSchool | null => state.schoolTeacher.school

/**
 * The header's shape, or `undefined` when nobody is signed in. Derived rather than stored, so the
 * persisted state holds the account as it arrived and recomposing a name needs no persist migration.
 */
export const selectTeacherProfile = (state: RootState): TeacherProfile | undefined => {
  const account = selectTeacherAccount(state)

  return account ? toTeacherProfile(account) : undefined
}

export default slice.reducer
